import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ShieldCheck, CheckCircle2, ArrowRight, AlertTriangle, LogOut, Users } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import { useAuth } from '../context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

const maskEmail = (e) => {
  if (!e || !e.includes('@')) return e || '';
  const [u, d] = e.split('@');
  const shown = u.length <= 2 ? u[0] : u.slice(0, 2);
  return `${shown}${'•'.repeat(Math.max(2, u.length - 2))}@${d}`;
};

const CommunityInvitePage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { user, isMember, login, logout, checkAuth } = useAuth();
  const [info, setInfo] = useState(null);
  const startedRef = useRef(false);

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    fetch(`${API}/api/community/invite/${token}`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : { valid: false }))
      .catch(() => ({ valid: false }))
      .then(setInfo);
  }, [token]);

  const invalid = info && info.valid === false;
  const inviteEmail = info?.email || '';
  const emailMismatch = !!inviteEmail && !!user?.email && user.email.toLowerCase() !== inviteEmail.toLowerCase() && !isMember;

  const handleSignOutRetry = async () => {
    await logout();
    await checkAuth();
    login(`/community/invite/${token}`);
  };

  let body;
  if (invalid) {
    body = (
      <div className="text-center py-10" data-testid="invite-invalid">
        <div className="mx-auto w-14 h-14 rounded-full bg-slate-100 border border-slate-200 flex items-center justify-center mb-4">
          <AlertTriangle className="w-7 h-7 text-slate-500" />
        </div>
        <h1 className="text-2xl font-extrabold text-slate-900">This invite link isn't valid</h1>
        <p className="text-slate-600 mt-2">The link may be mistyped or expired. Ask your host for a fresh invite, or apply below.</p>
        <button onClick={() => navigate('/community')} data-testid="invite-goto-community" className="mt-6 px-5 py-2.5 rounded-full bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800 transition-colors">Go to the community</button>
      </div>
    );
  } else if (isMember) {
    body = (
      <div className="text-center py-10" data-testid="invite-success">
        <div className="mx-auto w-16 h-16 rounded-full bg-emerald-50 border border-emerald-200 flex items-center justify-center mb-5">
          <CheckCircle2 className="w-8 h-8 text-emerald-600" />
        </div>
        <h1 className="text-3xl font-extrabold tracking-tight text-slate-900">You're in. Welcome!</h1>
        <p className="text-slate-600 mt-3 leading-relaxed">Your membership is active. Jump into the channels or start a direct message with a peer.</p>
        <div className="mt-7 flex flex-col sm:flex-row items-center justify-center gap-3">
          <button onClick={() => navigate('/community/inbox')} data-testid="invite-open-inbox" className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl bg-slate-900 text-white font-semibold hover:bg-slate-800 transition-colors">
            Open your inbox <ArrowRight className="w-4 h-4" />
          </button>
          <button onClick={() => navigate('/community')} className="w-full sm:w-auto inline-flex items-center justify-center px-6 py-3.5 rounded-xl border border-slate-200 text-slate-900 font-semibold hover:bg-slate-50 transition-colors">
            See the members
          </button>
        </div>
      </div>
    );
  } else if (emailMismatch) {
    body = (
      <div className="text-center py-8" data-testid="invite-mismatch">
        <div className="mx-auto w-14 h-14 rounded-full bg-amber-50 border border-amber-200 flex items-center justify-center mb-4">
          <AlertTriangle className="w-7 h-7 text-amber-600" />
        </div>
        <h1 className="text-2xl font-extrabold text-slate-900">Wrong email signed in</h1>
        <p className="text-slate-600 mt-2 leading-relaxed">
          You're signed in as <strong className="text-slate-900">{user?.email}</strong>, but this invite is for <strong className="text-slate-900">{maskEmail(inviteEmail)}</strong>. Sign out and continue with the invited email to get instant access.
        </p>
        <button onClick={handleSignOutRetry} data-testid="invite-switch-account" className="mt-6 inline-flex items-center gap-2 px-6 py-3.5 rounded-xl bg-slate-900 text-white font-semibold hover:bg-slate-800 transition-colors">
          <LogOut className="w-4 h-4" /> Sign out & use the invited email
        </button>
      </div>
    );
  } else {
    body = (
      <div className="text-center py-6" data-testid="invite-accept">
        <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-emerald-600 mb-4">
          <ShieldCheck className="w-4 h-4" /> Pre-approved invitation
        </div>
        <h1 className="text-4xl font-extrabold tracking-tight text-slate-900 leading-tight">You're invited to the Revenue Leaders Community</h1>
        <p className="mt-4 text-slate-600 leading-relaxed">
          A private, vetted room for revenue leaders. Your spot is reserved
          {inviteEmail ? <> for <strong className="text-slate-900">{maskEmail(inviteEmail)}</strong></> : null}
          {info?.title ? <> as <strong className="text-slate-900">{info.title}</strong></> : null}
          {info?.company ? <> at <strong className="text-slate-900">{info.company}</strong></> : null}.
        </p>
        <div className="mt-5 rounded-xl bg-slate-50 border border-slate-200 px-5 py-4 text-sm text-slate-600 text-left flex items-start gap-3">
          <Users className="w-5 h-5 text-slate-400 shrink-0 mt-0.5" />
          <span>Sign in with Google using the invited email and you're admitted instantly — no application, no waiting.</span>
        </div>
        <button onClick={() => login(`/community/invite/${token}`)} data-testid="invite-google-login" className="mt-6 w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl bg-slate-900 text-white font-semibold hover:bg-slate-800 transition-colors">
          Accept invite · Continue with Google
        </button>
        {inviteEmail ? <p className="mt-3 text-xs text-slate-400">Make sure to sign in with <strong>{maskEmail(inviteEmail)}</strong>.</p> : null}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <SEO title="Your invite · Revenue Leaders Community" description="Accept your invitation to the Revenue Leaders Community." path={`/community/invite/${token}`} noindex />
      <Header />
      <main className="pt-20 pb-24">
        <div className="max-w-lg mx-auto px-6">{body}</div>
      </main>
      <Footer />
    </div>
  );
};

export default CommunityInvitePage;
